import { Plus } from "lucide-react";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";

import { PanelLoading } from "~/components/loading";
import { Button } from "~/components/ui";
import { Authorization } from "~/features/authz/authorization";
import type {
  CompoundRecordVersionSearchCondition,
  CompoundRecordVersionSortField,
  SortDirection,
} from "~/features/compoundRecordVersions/types";
import { CompoundRecordVersionCreateDialog } from "./CompoundRecordVersionCreateDialog";
import { CompoundRecordVersionsSearchControls } from "./CompoundRecordVersionsSearchControls";
import { CompoundRecordVersionsTable } from "./CompoundRecordVersionsTable";

interface CompoundRecordVersionsPresenterProps {
  searchConditions: CompoundRecordVersionSearchCondition[];
  onConditionsChange: (next: CompoundRecordVersionSearchCondition[]) => void;
  onPageReset: () => void;
  sortField: CompoundRecordVersionSortField;
  direction: SortDirection;
  onSortingChange: (field: CompoundRecordVersionSortField, direction: SortDirection) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  isPending: boolean;
  createOpen: boolean;
  onOpenCreate: () => void;
  onCreateOpenChange: (open: boolean) => void;
}

export function CompoundRecordVersionsPresenter({
  searchConditions,
  onConditionsChange,
  onPageReset,
  sortField,
  direction,
  onSortingChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  isPending,
  createOpen,
  onOpenCreate,
  onCreateOpenChange,
}: CompoundRecordVersionsPresenterProps) {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-dewey-title capitalize text-fg-1">{t("model.compoundRecordVersion.modelNames")}</h1>
        <Authorization allowedRoles={["editor", "admin"]}>
          <Button type="button" size="sm" onClick={onOpenCreate}>
            <Plus />
            <span>{t("ui.addX", { x: t("model.compoundRecordVersion.modelName") })}</span>
          </Button>
        </Authorization>
      </div>

      <CompoundRecordVersionsSearchControls
        conditions={searchConditions}
        onConditionsChange={onConditionsChange}
        onPageReset={onPageReset}
      />

      <Suspense fallback={<PanelLoading />}>
        <CompoundRecordVersionsTable
          searchConditions={searchConditions}
          sortField={sortField}
          direction={direction}
          onSortingChange={onSortingChange}
          pageIndex={pageIndex}
          onPageIndexChange={onPageIndexChange}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          isPending={isPending}
        />
      </Suspense>

      <Authorization allowedRoles={["editor", "admin"]}>
        <CompoundRecordVersionCreateDialog open={createOpen} onOpenChange={onCreateOpenChange} />
      </Authorization>
    </div>
  );
}
